import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { ChevronDown, ChevronRight } from "lucide-react";
import { IoLanguageSharp } from "react-icons/io5";
// import CustomTooltip from "components/ContractEditing/CustomTooltip";
import { CLMButtonPrimary, CLMButtonPrimaryArrow } from "../v3/Buttons";
import { BiChevronRight } from "react-icons/bi";
import { MdKeyboardArrowDown } from "react-icons/md";
import CustomNavLink from "components/NavBar/CustomNavLink";
import { contractsForNavbar } from "@constants/contracts";
import { useRouter } from "next/router";
// Static image imports
import { BASE_ASSETS_URL } from "@constants/constants";
import GoogleTranslateWidget from "./GoogleTranslate";
import NvidiaNotification from "./NvidiaNotification";

const products = [
  {
    title: "Contract Lifecycle",
    subtitle: "BoloSign CLM",
    overview:
      "Create, track, and manage smarter contracts with automation, visibility, and compliance built-in from day one.",
    description: "Manage contracts across every stage in one place.",
    icon: `${BASE_ASSETS_URL}/v2/clm/navbar/products/icon_1.png`,
    image: `${BASE_ASSETS_URL}/v2/clm/navbar/products/1.png`,
    link: "/clm/contract-lifecycle",
    links: [
      { name: "Contract Editor", href: "/clm/editor" },
      { name: "Reporting", href: "/clm/reporting" },
      { name: "Analytics", href: "/clm/analytics" },
      { name: "Workflows", href: "/clm/workflow" },
      { name: "Repository", href: "/clm/repository" },
      { name: "Click Through", href: "/clm/clickThrough" },
    ],
  },
  {
    title: "AI Legal Assistant",
    subtitle: "Certius by BoloSign",
    overview:
      "Your AI-powered legal co-pilot, helping you identify risks, rewrite clauses, generate summaries, and suggest edits instantly.",
    // overviewLines: [
    //   "Transform contract process with AI",
    //   "Preview contract 20x faster with certius",
    //   "Create form with in seconds",
    // ],
    description:
      "Redline, review, & rewrite contracts faster with legal-trained AI.",
    icon: `${BASE_ASSETS_URL}/v2/clm/navbar/products/icon_2.png`,
    image: `${BASE_ASSETS_URL}/v2/clm/navbar/products/2.png`,
    link: "/clm/ai-assistant",
    links: [
      { name: "Certius", href: "/clm/certius-ai" },
      { name: "AI Verification", href: "/clm/ai-verification" },
    ],
  },
  {
    title: "eSignatures",
    subtitle: "BoloSign eSignature",
    overview:
      "Built for scale and trust. Collect signatures, enable clickwraps, and manage document execution effortlessly across teams.",
    description: "Capture consent and signatures with ease and flexibility.",
    icon: `${BASE_ASSETS_URL}/v2/clm/navbar/products/icon_3.png`,
    image: `${BASE_ASSETS_URL}/v2/clm/navbar/products/3.png`,
    link: "/clm/signature",
    links: [
      { name: "Templates", href: "/clm/templates" },
      { name: "Integrations", href: "/clm/integrations" },
      { name: "Legal Hub", href: "/clm/legal-hub" },
      { name: "eSignature", href: "/clm/esignatures" },
    ],
  },
];
const MobileProductSection = ({
  title,
  subtitle,
  description,
  links,
  icon,
  idx,
  setIsMenuOpen,
  link,
}) => (
  <div className={`py-7 ${idx === 0 ? "" : "border-t border-gray-200"}`}>
    <p className="text-sm font-semibold text-purple-700">{title}</p>
    <Link
      href={link}
      className="flex items-center justify-between space-x-2  py-5"
      onClick={() => setIsMenuOpen(false)}
    >
      <div className="flex items-center space-x-2">
        <Image src={icon} alt="Icon" className="h-16 w-auto" />
        <div>
          <h2 className="font-semibold">{subtitle}</h2>
          <p className="text-xs font-normal pr-12 text-gray-600 whitespace-pre-line">
            {description}
          </p>
        </div>
      </div>
      <div className="text-sm text-end pr-2">
        <span className="inline-block w-4 h-4 border-t-2 border-r-2 border-gray-700 rotate-45"></span>
      </div>
    </Link>
    <p className="text-xs font-semibold mb-2 text-purple-700">PLATFORM</p>
    <ul className="space-y-2 text-black">
      {links.map((link) => (
        <li key={link.name}>
          <Link href={link.href} onClick={() => setIsMenuOpen(false)}>
            {link.name}
          </Link>
        </li>
      ))}
    </ul>
  </div>
);
const DASHBOARD_URL_LOGIN = "https://signature.boloforms.com/login";

export default function CLMNavbar({ isDarkMode }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const [isProductOpen, setIsProductOpen] = useState(false);
  const [isResourcesOpen, setIsResourcesOpen] = useState(false);
  const [isCompanyOpen, setIsCompanyOpen] = useState(false);
  const navbarRef = useRef(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const router = useRouter();
  const SCROLL_COLOR_PATHS = ["/"];
  const shouldApplyScrollColor = SCROLL_COLOR_PATHS.some(
    (path) => router.pathname === path || router.pathname.startsWith(path + "/")
  );
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navbarRef.current && !navbarRef.current.contains(event.target)) {
        setIsProductOpen(false);
        setIsResourcesOpen(false);
        setIsCompanyOpen(false);
        setIsLanguageOpen(false);
        setIsMenuOpen(false);
        setIsContractsMenuOpen("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const languages = [
    { code: "ar", name: "Arabic" },
    { code: "bg", name: "Bulgarian" },
    { code: "zh-CN", name: "Chinese (Simplified)" },
    { code: "cs", name: "Czech" },
    { code: "da", name: "Danish" },
    { code: "nl", name: "Dutch" },
    { code: "en", name: "English" },
    { code: "fr", name: "French" },
    { code: "de", name: "German" },
    { code: "iw", name: "Hebrew" },
    { code: "id", name: "Indonesian" },
    { code: "it", name: "Italian" },
    { code: "ja", name: "Japanese" },
    { code: "ko", name: "Korean" },
    { code: "pt", name: "Portuguese" },
    { code: "ru", name: "Russian" },
    { code: "es", name: "Spanish" },
    { code: "th", name: "Thai" },
    { code: "tr", name: "Turkish" },
    { code: "uk", name: "Ukrainian" },
    { code: "vi", name: "Vietnamese" },
  ];

  const resources = [
    { name: "Blog", href: "/blog" },
    { name: "Case studies", href: "/customers" },
    // { name: "Ebooks & guides", href: "/ebooks-guides" },
    // { name: "Webinars", href: "/webinars" },
    // { name: "Product releases", href: "/product-releases" },
  ];

  const company = [
    { name: "About", href: "/about" },
    {
      name: "Guides",
      href: "https://help.boloforms.com/en/",
    },
    { name: "Roadmap", href: "https://boloforms.canny.io/" },
    { name: "Security", href: "https://security.boloforms.com/" },
    // { name: "Contact us", href: "/clm/request-demo" },
  ];
  let pathname = router.pathname;
  const renderContractsLink = pathname?.includes("/signature/contracts");
  const [isContractsMenuOpen, setIsContractsMenuOpen] = useState("");

  function FeatureItem({ icon, title, description, selected, onHover, link }) {
    const [isHovered, setIsHovered] = useState(false);
    return (
      <div
        className={`flex items-center justify-between cursor-pointer p-8 ${
          selected ? "bg-[#f9f9f7]" : "bg-[#f2f1ee]"
        } hover:bg-[#f9f9f7]`}
        onMouseEnter={() => {
          setIsHovered(true);
          console.log("hovered");
          onHover();
        }}
        onMouseLeave={() => {
          setIsHovered(false);
          console.log("left");
        }}
        onClick={() => {
          setIsProductOpen(false);
          setIsResourcesOpen(false);
          setIsCompanyOpen(false);
          setIsLanguageOpen(false);
          setIsMenuOpen(false);
          setIsContractsMenuOpen("");
          // also want to navigate to link
          router.push(link);
        }}
      >
        <div className="flex items-center space-x-4">
          <div
            className={`text-2xl px-1 ${
              isHovered ? "bg-dark" : "bg-surface-dark"
            } h-16 w-16 rounded-lg flex items-center justify-center`}
          >
            <Image src={icon} alt={title} width={40} height={40} className="" />
          </div>
          <div className="w-3/5">
            <h4 className="text-gray-900">{title}</h4>
            <p className="text-gray-600 text-xs">{description}</p>
          </div>
        </div>
        <div className="text-sm text-end">
          <span className="inline-block w-4 h-4 border-t-2 border-r-2 border-gray-700 rotate-45"></span>
        </div>
      </div>
    );
  }

  function ProductDropdown() {
    const [selected, setSelected] = useState("Contract Lifecycle");

    const selectedProduct = products.find((p) => p.title === selected);

    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 bg-white text-gray-800 rounded-xl">
        {/* Left Column */}
        <div>
          {products.map((product) => (
            <FeatureItem
              key={product.title}
              title={product.title}
              description={product.description}
              icon={product.icon}
              selected={selected === product.title}
              onHover={() => setSelected(product.title)}
              link={product.link}
            />
          ))}
        </div>

        {/* Middle Column */}
        {selectedProduct && (
          <div className="text-center md:text-left p-6">
            <p className="text-purple-700 text-xs mb-4 pl-1">OVERVIEW</p>
            <div className="rounded-lg overflow-hidden mb-4">
              <Image
                src={selectedProduct.image}
                alt={selectedProduct.title}
                height={250}
                width={250}
                className="rounded-md"
              />
            </div>
            <h3 className="text-gray-800 font-semibold text-sm">
              {selectedProduct.subtitle}
            </h3>
            {selectedProduct.overview && (
              <p className="text-xs font-normal mb-2">
                {selectedProduct.overview}
              </p>
            )}
            {selectedProduct.overviewLines &&
              selectedProduct.overviewLines.map((line, idx) => (
                <p className="text-xs font-normal" key={idx}>
                  {line}
                </p>
              ))}
            {/* this below anchor is making the whole page reload what else we can use */}
            <Link
              href={selectedProduct.link}
              onClick={() => {
                setIsProductOpen(false);
                setIsResourcesOpen(false);
                setIsCompanyOpen(false);
                setIsLanguageOpen(false);
                setIsMenuOpen(false);
                setIsContractsMenuOpen("");
              }}
              className="text-gray-600 underline text-xs mt-2 inline-block"
            >
              Learn more
            </Link>
          </div>
        )}

        {/* Right Column */}
        {selectedProduct && selectedProduct.links?.length > 0 && (
          <div className="p-6 w-full ">
            <p className="text-purple-700 text-xs mb-4 pl-4">PLATFORM</p>
            <ul className=" text-gray-700 w-full">
              {selectedProduct.links.map((link) => (
                <li key={link.href} className="w-full">
                  <Link
                    href={link.href}
                    onClick={() => {
                      setIsProductOpen(false);
                      setIsResourcesOpen(false);
                      setIsCompanyOpen(false);
                      setIsLanguageOpen(false);
                      setIsMenuOpen(false);
                      setIsContractsMenuOpen("");
                    }}
                    className="hover:bg-gray-100 px-4 py-2 rounded w-full flex justify-start items-center"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };

    // Only add scroll listener for specific routes
    if (SCROLL_COLOR_PATHS.some((path) => router.pathname === path)) {
      window.addEventListener("scroll", handleScroll);
      handleScroll(); // initialize state
    }

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [router.pathname]);

  return (
    <>
      <div
        className={`bg-dark flex justify-center md:justify-between items-center fixed top-0 left-0 right-0 z-20 px-4 sm:p-4 overflow-hidden h-8`}
      >
        <div className="hidden md:block text-white text-xs">
          <Link
            href="/signature/pricing"
            className="flex items-center hover:text-gray-300"
          >
            Buy Now
            <BiChevronRight className="text-2xl" />
          </Link>
        </div>
        <div className="flex flex-row items-center">
          <a
            href={`tel:+14156496611`}
            className="text-white hover:text-gray-300 text-xs flex items-center gap-2"
          >
            Sales: +1 (415) 6496611
          </a>
          <button
            onClick={() => {
              window.open(DASHBOARD_URL_LOGIN, "_blank");
            }}
            className="ml-4 mr-1.5 text-white hover:text-gray-300 text-xs hidden md:block"
          >
            Sign In
          </button>
        </div>
      </div>
      <nav
        ref={navbarRef}
        className={`${
          (shouldApplyScrollColor && !isScrolled) || isDarkMode
            ? isDarkMode
              ? "bg-dark text-white border-b border-gray-200"
              : "bg-dark text-white"
            : "bg-white text-gray-700 border-b border-gray-200"
        } hover:bg-white hover:text-dark lg:shadow-sm fixed top-8 z-50 w-full transition-colors duration-500 group `}
      >
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-20">
            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8">
              <Link
                href="/"
                onClick={() => {
                  setIsProductOpen(false);
                  setIsResourcesOpen(false);
                  setIsCompanyOpen(false);
                  setIsLanguageOpen(false);
                  setIsMenuOpen(false);
                  setIsContractsMenuOpen("");
                }}
              >
                <Image
                  src={`${BASE_ASSETS_URL}/v2/clm/clm/BoloSign.png`}
                  alt="BoloSign Logo"
                  className={`${
                    (shouldApplyScrollColor && !isScrolled) || isDarkMode
                      ? "invert"
                      : "invert-0"
                  } group-hover:invert-0 h-6 w-auto`}
                  width={100}
                  height={100}
                />
              </Link>
              <div className="relative">
                <button
                  onMouseEnter={() => {
                    setIsProductOpen(!isProductOpen);
                    setIsResourcesOpen(false);
                    setIsCompanyOpen(false);
                    setIsLanguageOpen(false);
                    setIsContractsMenuOpen("");
                  }}
                  className=" hover:text-blue-600 text-base flex items-center font-semibold"
                >
                  Products
                  <ChevronDown className="ml-1 h-4 w-4" />
                </button>
                {isProductOpen && (
                  <div className="absolute z-20  top-[52px] -left-[176px] mt-0 xxl:w-[1200px] lg:w-[900px] bg-white border rounded-t-0 rounded-b-2xl shadow-lg flex gap-[20px] overflow-hidden">
                    <ProductDropdown setIsProductOpen={setIsProductOpen} />
                  </div>
                )}
              </div>
              {/* Hiding the Company and Resources links on contracts Page */}
              {!renderContractsLink && (
                <div className="relative">
                  <button
                    onMouseEnter={() => {
                      setIsResourcesOpen(!isResourcesOpen);
                      setIsProductOpen(false);
                      setIsCompanyOpen(false);
                      setIsLanguageOpen(false);
                      setIsContractsMenuOpen("");
                    }}
                    className=" hover:text-blue-600 text-base font-semibold flex items-center"
                  >
                    Resources
                    <ChevronDown className="ml-1 h-4 w-4" />
                  </button>
                  {isResourcesOpen && (
                    <div className="absolute z-20 -left-[12px] top-[52px] rounded-t-0 rounded-b-2xl mt-0 w-60 bg-white border  shadow-lg p-3">
                      {resources.map((resource) => (
                        <Link
                          key={resource.name}
                          href={resource.href}
                          className="block px-4 py-2 text-sm text-gray-900 hover:bg-gray-100 rounded-md"
                          onClick={() => setIsResourcesOpen(false)}
                        >
                          {resource.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {!renderContractsLink && (
                <div className="relative">
                  <button
                    onMouseEnter={() => {
                      setIsCompanyOpen(!isCompanyOpen);
                      setIsProductOpen(false);
                      setIsResourcesOpen(false);
                      setIsLanguageOpen(false);
                      setIsContractsMenuOpen("");
                    }}
                    className=" hover:text-blue-600 text-base font-semibold flex items-center"
                  >
                    Company
                    <ChevronDown className="ml-1 h-4 w-4" />
                  </button>
                  {isCompanyOpen && (
                    <div className="absolute z-20 -left-[12px] top-[52px] rounded-t-0 rounded-b-2xl mt-0 w-60 bg-white border  shadow-lg p-3">
                      {company.map((company) => (
                        <Link
                          key={company.name}
                          href={company.href}
                          className="block px-4 py-2 text-sm  hover:bg-gray-100 rounded-md"
                          onClick={() => setIsCompanyOpen(false)}
                        >
                          {company.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {/* Only On Contracts Page */}
              {renderContractsLink &&
                Object.keys(contractsForNavbar).map((nav, index) => (
                  <CustomNavLink
                    key={index}
                    rel="noreferrer"
                    onClick={() => {
                      if (isContractsMenuOpen === nav) {
                        setIsContractsMenuOpen("");
                      } else {
                        setIsContractsMenuOpen(nav);
                      }
                      setIsCompanyOpen(false);
                      setIsProductOpen(false);
                      setIsResourcesOpen(false);
                      setIsLanguageOpen(false);
                    }}
                  >
                    {" "}
                    {nav} <MdKeyboardArrowDown className="text-xl  mt-1" />
                  </CustomNavLink>
                ))}
              <Link
                href="/signature/pricing"
                className=" hover:text-blue-600 text-base font-semibold"
                onClick={() => {
                  setIsCompanyOpen(false);
                  setIsProductOpen(false);
                  setIsResourcesOpen(false);
                  setIsLanguageOpen(false);
                  setIsContractsMenuOpen("");
                }}
              >
                Pricing
              </Link>
            </div>
            {/* Desktop Actions */}
            <div className="hidden lg:flex items-center space-x-4">
              {/* Language Selector */}
              <button
                // onClick={() => {
                //   setIsLanguageOpen(!isLanguageOpen);
                //   setIsProductOpen(false);
                //   setIsResourcesOpen(false);
                //   setIsCompanyOpen(false);
                //   setIsContractsMenuOpen("");
                // }}
                className="flex items-center  hover:text-blue-600 hover:bg-blue-50 rounded-full p-[6px] text-base font-semibold relative w-11 h-11 justify-center"
              >
                <IoLanguageSharp className={`text-lg md:text-xl`} />
                <GoogleTranslateWidget />
              </button>

              {/* Login and Contact Buttons */}
              <Link
                href=""
                onClick={() => {
                  window.open(DASHBOARD_URL_LOGIN, "_blank");
                }}
                className=" hover:text-blue-600 text-base font-semibold"
              >
                Login
              </Link>

              {/* NVIDIA Inception Program Notification */}
              <NvidiaNotification />

              {/* <Link
              href="/contact"
              className="bg-blue-600 text-white px-6 py-3 rounded-md text-sm font-semibold"
              style={{
                background: "linear-gradient(275deg, #c86aff -8.37%, #3164ff)",
              }}
            >
              Contact Sales
            </Link> */}
              <CLMButtonPrimary
                buttonText="Contact Sales"
                buttonUrl="/clm/request-demo"
              />
            </div>
            {isContractsMenuOpen && (
              <Contracts
                currentPopUp={isContractsMenuOpen}
                setIsContractsMenuOpen={setIsContractsMenuOpen}
              />
            )}
            {(isCompanyOpen ||
              isProductOpen ||
              isResourcesOpen ||
              isContractsMenuOpen ||
              isLanguageOpen) && (
              <div
                className="w-full h-full fixed top-[112px] left-0 z-10 bg-black/30 lg:block hidden"
                onClick={() => {
                  setIsProductOpen(false);
                  setIsResourcesOpen(false);
                  setIsLanguageOpen(false);
                  setIsCompanyOpen(false);
                  setIsContractsMenuOpen("");
                }}
              ></div>
            )}
            {/* Mobile Menu Button and icon */}
            <div className="lg:hidden flex items-center justify-between w-full">
              <Link
                href="/"
                onClick={() => {
                  setIsMenuOpen(false);
                }}
              >
                <Image
                  src={`${BASE_ASSETS_URL}/v2/clm/clm/BoloSign.png`}
                  alt="BoloSign Logo"
                  className={`${
                    (shouldApplyScrollColor && !isScrolled) || isDarkMode
                      ? "invert"
                      : "invert-0"
                  } group-hover:invert-0 h-6 w-auto`}
                  width={100}
                  height={100}
                />
              </Link>

              <div className="flex items-center gap-4">
                <NvidiaNotification />
                <button
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className="relative w-8 h-8 flex items-center justify-center focus:outline-none"
                >
                  <span
                    className={`absolute h-0.5 w-7 ${
                      (shouldApplyScrollColor && !isScrolled) || isDarkMode
                        ? "bg-white"
                        : "bg-dark"
                    } group-hover:bg-dark  transition-transform duration-300 ease-in-out origin-center ${
                      isMenuOpen ? "rotate-45 translate-y-0" : "-translate-y-2"
                    }`}
                  />
                  <span
                    className={`absolute h-0.5 w-7 ${
                      (shouldApplyScrollColor && !isScrolled) || isDarkMode
                        ? "bg-white"
                        : "bg-dark"
                    } group-hover:bg-dark transition-opacity duration-300 ease-in-out ${
                      isMenuOpen ? "opacity-0" : "opacity-100"
                    }`}
                  />
                  <span
                    className={`absolute h-0.5 w-7 ${
                      (shouldApplyScrollColor && !isScrolled) || isDarkMode
                        ? "bg-white"
                        : "bg-dark"
                    } group-hover:bg-dark transition-transform duration-300 ease-in-out origin-center ${
                      isMenuOpen ? "-rotate-45 translate-y-0" : "translate-y-2"
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile view */}
        {isMenuOpen && (
          <div className="lg:hidden bg-white border-t h-[calc(100vh-80px)] overflow-y-auto scrollbar-thin ">
            <div className=" mb-4 pt-2 pb-3 space-y-1">
              <div
                className="block text-black text-base font-semibold py-2 border-b border-gray-200 px-4"
                onClick={() => {
                  setIsProductOpen(!isProductOpen);
                }}
              >
                <span className="uppercase font-semibold">Products</span>
                <ChevronDown className="ml-1 h-5 w-5 inline-block float-right" />
                {isProductOpen && (
                  <div className="py-4">
                    {products.map((product, idx) => (
                      <MobileProductSection
                        key={idx}
                        idx={idx}
                        {...product}
                        setIsMenuOpen={setIsMenuOpen}
                      />
                    ))}
                  </div>
                )}
              </div>
              <div
                className="block text-black text-base font-semibold py-2 border-b border-gray-200 px-4"
                onClick={() => {
                  setIsResourcesOpen(!isResourcesOpen);
                }}
              >
                <span className="uppercase font-semibold">Resources</span>
                <ChevronDown className="ml-1 h-5 w-5 inline-block float-right" />
                {isResourcesOpen && (
                  <div className="py-4">
                    {resources.map((resource) => (
                      <Link
                        key={resource.name}
                        href={resource.href}
                        className="block py-2 text-gray-800 hover:bg-gray-100 px-3 rounded-md"
                        onClick={() => {
                          setIsMenuOpen(false);
                          setIsResourcesOpen(false);
                        }}
                      >
                        {resource.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              {/* <div className="block text-black text-base font-semibold py-2 border-b border-gray-200 px-4">
                <span className="uppercase font-semibold">Customers</span>
                <ChevronRight className="ml-1 h-5 w-5 inline-block float-right" />
              </div> */}
              <div
                className="block text-black text-base font-semibold py-2 border-b border-gray-200 px-4"
                onClick={() => setIsCompanyOpen(!isCompanyOpen)}
              >
                <span className="uppercase font-semibold">Company</span>
                <ChevronDown className="ml-1 h-5 w-5 inline-block float-right" />
                {isCompanyOpen && (
                  <div className="py-4">
                    {company.map((company) => (
                      <Link
                        key={company.name}
                        href={company.href}
                        className="block py-2 text-gray-800 hover:bg-gray-100  px-3 rounded-md"
                        onClick={() => {
                          setIsMenuOpen(false);
                          setIsCompanyOpen(false);
                        }}
                      >
                        {company.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              {renderContractsLink &&
                Object.keys(contractsForNavbar).map((nav, index) => {
                  const navItem =
                    nav === "Real Estate"
                      ? "real-estate"
                      : nav === "Personal Family"
                      ? "personal-family"
                      : nav === "Business"
                      ? "business"
                      : null;
                  return (
                    <div
                      key={index}
                      rel="noreferrer"
                      onClick={() => {
                        router.push(`/signature/contracts/${navItem}`);
                        setIsMenuOpen(false);
                      }}
                      className="block text-black text-base font-semibold py-3 border-b border-gray-200 px-4"
                    >
                      <span className="uppercase font-semibold"> {nav}</span>
                      <ChevronRight className="ml-1 h-5 w-5 inline-block float-right" />
                    </div>
                  );
                })}
              <Link
                href={"/signature/pricing"}
                className="block text-black text-base font-semibold py-2 border-b border-gray-200 px-4"
                onClick={() => {
                  setIsMenuOpen(false);
                }}
              >
                <span className="uppercase font-semibold">Pricing</span>
                <ChevronRight className="ml-1 h-5 w-5 inline-block float-right" />
              </Link>

              <Link
                href={DASHBOARD_URL_LOGIN}
                className="block text-black text-base font-semibold py-2 px-4 border-b border-gray-200"
              >
                <span className="uppercase font-semibold">Login</span>
                <ChevronRight className="ml-1 h-5 w-5 inline-block float-right" />
              </Link>
            </div>

            <div className="flex flex-col gap-2 px-4 mb-10">
              {/* contact sales button */}
              <CLMButtonPrimary
                buttonText="Contact Sales"
                buttonUrl="/clm/request-demo"
                textSize="text-lg"
                onClick={() => {
                  setIsMenuOpen(false);
                }}
              />
            </div>
          </div>
        )}
      </nav>
    </>
  );
}
const Contracts = ({ currentPopUp, setIsContractsMenuOpen }) => {
  const addHyphenInUrl = (str) => {
    return str.replace(/\s+/g, "-").toLowerCase();
  };

  const generateSlug = (text) => {
    return text
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^\w-]+/g, "");
  };

  let router = useRouter();

  return (
    <div className="fixed z-50  top-[112px] xxl:left-[calc(50%_-_620px)]  mt-0 xxl:w-[1240px] lg:w-[100%] bg-white border rounded-t-0 rounded-b-2xl shadow-lg  gap-[20px] overflow-hidden lg:flex hidden">
      <div className="grid grid-cols-4">
        <div
          className={`col-span-1 py-4 px-6  bg-[#F2F1EE] border-r border-gray-200`}
        >
          <h3 className="text-2xl font-semibold">{currentPopUp}</h3>
          <p className="text-base mt-2 leading-snug">
            {contractsForNavbar[currentPopUp]?.description}
          </p>
        </div>
        <div className="col-span-3 right flex flex-col justify-between text-black">
          <h2 className="font-semibold text-xl border-b p-4">
            Top {contractsForNavbar[currentPopUp].heading} Forms
          </h2>
          <div className="grid grid-cols-4 gap-6 p-4">
            {Object.keys(contractsForNavbar[currentPopUp]?.subCategoryData).map(
              (subContract, index) => (
                <div key={index} className="flex flex-col gap-4">
                  <h4 className="text-base text-gray-700 font-semibold">
                    {subContract.toUpperCase()}
                  </h4>
                  <div className="flex flex-col gap-2 text-sm">
                    {contractsForNavbar[currentPopUp].subCategoryData[
                      subContract
                    ].map((contract, innerIndex) => (
                      <p
                        key={innerIndex}
                        className="hover:text-violet-700 cursor-pointer"
                        onClick={() => {
                          router.push(
                            `/signature/contracts/${addHyphenInUrl(
                              contractsForNavbar[currentPopUp].heading
                            )}/${generateSlug(contract.name)}`
                          );
                          setIsContractsMenuOpen("");
                        }}
                      >
                        {contract.name}
                      </p>
                    ))}
                  </div>
                </div>
              )
            )}
          </div>
          <div className="font-normal border-t p-4 pr-8 flex justify-end">
            <CLMButtonPrimaryArrow
              buttonText={contractsForNavbar[currentPopUp].heading + " Forms"}
              onClick={() => {
                router.push(
                  `/signature/contracts/${addHyphenInUrl(
                    contractsForNavbar[currentPopUp].heading
                  )}`
                );
                setIsContractsMenuOpen("");
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
